/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Observable, of, switchMap } from 'rxjs';
import { BaseService } from '../../../Services/_base';
import { HttpResponse } from '../../../Services/http';
import {
  EntityTenantSetting,
  EntityTenantSettings,
  EntityTenantSettingsSchema,
  EntityTenantSettingsSchemaList,
} from './Entities';
import { TenantSettingsServiceConfiguration } from './TenantSettings.service.config';
import {
  TenantSettingsServiceDeleteSettingDto,
  TenantSettingsServiceGetSchemaDto,
  TenantSettingsServiceGetSchemaListDto,
  TenantSettingsServiceGetSettingDto,
  TenantSettingsServiceGetSettingsListDto,
  TenantSettingsServicePutSettingDto,
} from './TenantSettings.service.dto';

/**
 * The tenant settings service.
 */
export class TenantSettingsService extends BaseService {
  public constructor(configuration: TenantSettingsServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Tenant Settings service.
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`tenant-settings`);
  }

  /**
   * Creates api url for schemas.
   */
  public createSchemasUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/api/v1/schemas`;
  }

  /**
   * Creates api url for settings.
   */
  public createSettingsUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/api/v1/settings`;
  }

  /**
   * Attempt to delete a setting by service ID and setting key.
   */
  public deleteSetting(dto: TenantSettingsServiceDeleteSettingDto): Observable<null> {
    const { schemaId, serviceId, settingKey } = dto;

    const baseSettingsUrl = this.createSettingsUrl();
    if (!baseSettingsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(baseSettingsUrl, {
        ...this.getSessionRequestOptions(),
        body: {
          schemaId,
          serviceId,
          settingKey,
        },
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<null> => {
          return this._handleResponseError<null>(value);
        }),
      );
  }

  /**
   * Attempt to get a schema by schema ID.
   */
  public getSchema(dto: TenantSettingsServiceGetSchemaDto): Observable<EntityTenantSettingsSchema | null> {
    const { schemaId } = dto;

    const queryParams: Array<string> = [];

    if (schemaId) {
      queryParams.push(`${encodeURIComponent('schemaId')}=${schemaId}`);
    }

    const baseSchemasUrl = this.createSchemasUrl();
    if (!baseSchemasUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSchemasUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityTenantSettingsSchema> => {
          return this._handleResponseError<EntityTenantSettingsSchema>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of available schemas.
   */
  public getSchemaList(dto: TenantSettingsServiceGetSchemaListDto): Observable<EntityTenantSettingsSchemaList | null> {
    const { tenantType } = dto;

    const queryParams: Array<string> = [];
    queryParams.push(`${encodeURIComponent('filter[tenantType]')}=${tenantType}`);

    const baseSchemasUrl = this.createSchemasUrl();
    if (!baseSchemasUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, `${baseSchemasUrl}/list`);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityTenantSettingsSchemaList> => {
          return this._handleResponseError<EntityTenantSettingsSchemaList>(value);
        }),
      );
  }

  /**
   * Attempt to get a setting by service ID and setting key.
   */
  public getSetting(dto: TenantSettingsServiceGetSettingDto): Observable<EntityTenantSetting | null> {
    const { schemaId, serviceId, settingKey } = dto;

    const queryParams: Array<string> = [];

    if (schemaId) {
      queryParams.push(`${encodeURIComponent('schemaId')}=${schemaId}`);
    }

    if (serviceId) {
      queryParams.push(`${encodeURIComponent('serviceId')}=${serviceId}`);
    }

    if (settingKey) {
      queryParams.push(`${encodeURIComponent('settingKey')}=${settingKey}`);
    }

    const baseSettingsUrl = this.createSettingsUrl();
    if (!baseSettingsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSettingsUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityTenantSetting> => {
          return this._handleResponseError<EntityTenantSetting>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of settings.
   * @template T - Type of the Tenant Setting value - optional for stronger typing.
   */
  public getSettingsList<T = unknown>(
    dto: TenantSettingsServiceGetSettingsListDto,
  ): Observable<EntityTenantSettings<T> | null> {
    const { schemaId, serviceId, pageAfter, pageBefore, pageSize, showHiddenValues } = dto;

    const queryParams: Array<string> = [];

    if (!showHiddenValues) {
      queryParams.push(`${encodeURIComponent('filter[uiDisplay]')}=true`);
    }

    if (serviceId) {
      queryParams.push(`${encodeURIComponent('filter[serviceId]')}=${serviceId}`);
    }

    if (schemaId) {
      queryParams.push(`${encodeURIComponent('filter[schemaId]')}=${schemaId}`);
    }

    if (pageSize) {
      queryParams.push(`${encodeURIComponent('page[size]')}=${pageSize}`);
    }

    if (pageBefore) {
      queryParams.push(`${encodeURIComponent(`page[before]`)}=${pageBefore}`);
    }

    if (pageAfter) {
      queryParams.push(`${encodeURIComponent(`page[after]`)}=${pageAfter}`);
    }

    const baseSettingsUrl = this.createSettingsUrl();
    if (!baseSettingsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, `${baseSettingsUrl}/list`);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityTenantSettings<T>> => {
          return this._handleResponseError<EntityTenantSettings<T>>(value);
        }),
      );
  }

  /**
   * Attempt to create/update a setting.
   */
  public putSetting(dto: TenantSettingsServicePutSettingDto): Observable<EntityTenantSetting | null> {
    const { schemaId, serviceId, settingKey, value } = dto;

    const baseSettingsUrl = this.createSettingsUrl();
    if (!baseSettingsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .put<unknown, HttpResponse>(baseSettingsUrl, {
        ...this.getSessionRequestOptions(),
        body: {
          schemaId,
          serviceId,
          settingKey,
          value,
        },
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityTenantSetting> => {
          return this._handleResponseError<EntityTenantSetting>(value);
        }),
      );
  }
}
